import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginationParams } from '../types/common';
import { 
  UpdateOrganizationFileDto,
  UploadOrganizationFileDto 
} from '../types/dtos/training';
import { createOrganizationFileService } from '../services/organization-file.service';

const ORGANIZATION_FILES_KEY = 'organization-files';

// List all files
export const useOrganizationFiles = (
  organizationId: string,
  params?: PaginationParams
) => {
  const fileService = createOrganizationFileService(organizationId);

  return useQuery({
    queryKey: [ORGANIZATION_FILES_KEY, organizationId, params],
    queryFn: () => fileService.findAll(params).then((res) => res.data),
    enabled: !!organizationId
  });
};

// List files by entity
export const useOrganizationFilesByEntity = (
  organizationId: string,
  entityType: string,
  entityId: string,
  params?: PaginationParams,
  options?: { enabled?: boolean }
) => {
  const fileService = createOrganizationFileService(organizationId);
  const enabled = options?.enabled !== undefined ? options.enabled : (!!organizationId && !!entityType && !!entityId);

  return useQuery({
    queryKey: [ORGANIZATION_FILES_KEY, organizationId, 'entity', entityType, entityId, params],
    queryFn: () => fileService.findByEntity(entityType, entityId, params).then((res) => res.data),
    enabled
  });
};

// Get single file
export const useOrganizationFile = (
  organizationId: string,
  fileId: string
) => {
  const fileService = createOrganizationFileService(organizationId);

  return useQuery({
    queryKey: [ORGANIZATION_FILES_KEY, organizationId, fileId],
    queryFn: () => fileService.findById(fileId).then((res) => res.data),
    enabled: !!organizationId && !!fileId
  });
};

// Upload file
export const useUploadOrganizationFile = (organizationId: string) => {
  const queryClient = useQueryClient();
  const fileService = createOrganizationFileService(organizationId);

  return useMutation({
    mutationFn: (data: UploadOrganizationFileDto) => {
      // Build FormData from the DTO
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('entityId', data.entityId);
      formData.append('entityType', data.entityType);
      
      if (data.description) formData.append('description', data.description);

      return fileService.uploadFile(formData).then((res) => res.data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_FILES_KEY, organizationId] });
      if (data.entityType && data.entityId) {
        queryClient.invalidateQueries({ 
          queryKey: [ORGANIZATION_FILES_KEY, organizationId, 'entity', data.entityType, data.entityId] 
        });
      }
    }
  });
};

// Update file metadata
export const useUpdateOrganizationFile = (organizationId: string) => {
  const queryClient = useQueryClient();
  const fileService = createOrganizationFileService(organizationId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationFileDto }) =>
      fileService.update(id, data).then((res) => res.data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_FILES_KEY, organizationId] });
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_FILES_KEY, organizationId, id] });
      if (data.entityType && data.entityId) {
        queryClient.invalidateQueries({ 
          queryKey: [ORGANIZATION_FILES_KEY, organizationId, 'entity', data.entityType, data.entityId] 
        });
      }
    }
  });
};

// Delete file
export const useDeleteOrganizationFile = (organizationId: string) => {
  const queryClient = useQueryClient();
  const fileService = createOrganizationFileService(organizationId);

  return useMutation({
    mutationFn: (id: string) => fileService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_FILES_KEY, organizationId] });
    }
  });
};

